import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { getCandidatos, getUbicaciones, busquedaIA, updateCandidato, deleteCandidato, getListas, createLista, deleteLista, updateLista, getCandidatosDeLista, setCarpetaEnLista, setRevisadorEnLista, isAuthenticated, INTEREST_WEIGHTS_UPDATED_EVENT, INTEREST_WEIGHTS_STORAGE_KEY, verifyAdminPassword, sendEmailDescartadosTest, sendEmailToDescartados, updateEmailDescartadosDraft } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { useComparison } from "@/contexts/ComparisonContext";
import { useUnopenedCandidatos } from "@/contexts/UnopenedCandidatosContext";
import { UserAvatar } from "@/components/UserAvatar";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Search, Filter, X, Loader2, Users, MapPin, Sparkles, PhoneCall, Archive, Trash2, ArrowUpDown, ListChecks, Plus, ChevronLeft, Pencil, Check, Heart, LayoutGrid, UserPlus, Download, FileQuestion, ShieldCheck, Mail, CheckCircle2, XCircle, RefreshCw, MoreVertical, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AppMainHeader } from "@/components/AppMainHeader";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import EmojiPicker from "emoji-picker-react";

// Misma lista que el backend (SEGMENTOS_CATALOGO); el filtro "Gestión de Redes Sociales" incluye también Content Creator, Social Media Manager, etc.
const SEGMENTOS = [
  "Gestión de Redes Sociales",
  "Videógrafo",
  "Fotógrafo",
  "Diseño",
  "Paid Media",
  "SEO",
  "Copywriting",
  "Estrategia",
  "Desarrollo Web",
  "Director Creativo",
  "Brand Manager",
  "Brand Strategist",
  "Planner",
  "Account Manager",
  "Analista de Mercado",
  "Director de Arte",
  "Diseñador UX/UI",
  "Ilustrador",
  "Trafficker",
  "Media Planner",
  "SEM Specialist",
  "Community Manager",
  "Social Media Manager",
  "Content Manager",
  "Content Creator",
  "Redactor Creativo",
  "Guionista",
  "Content Marketing",
  "Editor de video",
  "Motion Graphics",
  "Desarrollador",
  "E-commerce",
  "Fotografía de producto",
  "Retoque",
  "Dirección de arte",
  "Dirección de fotografía",
];

const NIVELES = ["Junior", "Mid", "Senior"];

const REVISADOR_COLORS = [
  "#3b82f6", "#8b5cf6", "#ec4899", "#ef4444", "#f97316",
  "#eab308", "#22c55e", "#14b8a6", "#06b6d4", "#6366f1",
];

/** Oculta la UI de Revisadores (la app usa usuarios de sesión para avatares). El código se mantiene por si se reactiva. */
const SHOW_REVISADORES_UI = false;

function parseSearchParams(searchParams) {
  const get = (k, def) => searchParams.get(k) ?? def;
  const getNum = (k, def) => {
    const v = searchParams.get(k);
    if (v == null || v === "") return def;
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? def : n;
  };
  return {
    busqueda: get("busqueda", ""),
    segmento: get("segmento", ""),
    nivel: get("nivel", ""),
    experiencia: [Math.max(0, getNum("exp_min", 0)), Math.min(20, getNum("exp_max", 20))],
    minScore: Math.max(0, getNum("min_score", 0)),
    rangoSalarial: [Math.max(0, getNum("sal_min", 0)), Math.min(100, getNum("sal_max", 100))],
    ubicacion: get("ubicacion", ""),
    situacionActual: get("situacion", "all"),
    carnetConducir: get("carnet", "") === "1",
    vehiculoPropio: get("vehiculo", "") === "1",
    ordenarPor: get("ordenar", "nuevos"),
  };
}

export default function CandidatosPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [candidatos, setCandidatos] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [isFiltersClosing, setIsFiltersClosing] = useState(false);
  const [ubicaciones, setUbicaciones] = useState([]);
  
  // IA Search
  const [iaQuery, setIaQuery] = useState("");
  const [iaResults, setIaResults] = useState(null);
  const [isSearchingIA, setIsSearchingIA] = useState(false);
  
  // Filters: inicializar desde URL para que al volver atrás se mantengan
  const paramsFromUrl = useMemo(() => parseSearchParams(searchParams), [searchParams]);
  const [busqueda, setBusqueda] = useState(() => paramsFromUrl.busqueda);
  const [segmento, setSegmento] = useState(() => paramsFromUrl.segmento);
  const [nivel, setNivel] = useState(() => paramsFromUrl.nivel);
  const [experiencia, setExperiencia] = useState(() => paramsFromUrl.experiencia);
  const [minScore, setMinScore] = useState(() => paramsFromUrl.minScore);
  const [rangoSalarial, setRangoSalarial] = useState(() => paramsFromUrl.rangoSalarial);
  const [ubicacion, setUbicacion] = useState(() => paramsFromUrl.ubicacion);
  const [situacionActual, setSituacionActual] = useState(() => paramsFromUrl.situacionActual);
  const [carnetConducir, setCarnetConducir] = useState(() => paramsFromUrl.carnetConducir);
  const [vehiculoPropio, setVehiculoPropio] = useState(() => paramsFromUrl.vehiculoPropio);
  const [ordenarPor, setOrdenarPor] = useState(() => paramsFromUrl.ordenarPor);
  const [tabActivos, setTabActivos] = useState(true);
  const [tabFavoritos, setTabFavoritos] = useState(false);
  const [tabListas, setTabListas] = useState(false);
  const [listas, setListas] = useState([]);
  const [listaActiva, setListaActiva] = useState(null); // {id, nombre, candidatos_ids}
  // Móvil: el sidebar (Activos/Favoritos/Archivados/listas) se abre como drawer desde la barra de acciones
  const [mobileViewsOpen, setMobileViewsOpen] = useState(false);
  useEffect(() => {
    setMobileViewsOpen(false);
  }, [tabActivos, tabFavoritos, tabListas, listaActiva?.id]);
  const [candidatosLista, setCandidatosLista] = useState([]);
  const [isLoadingListas, setIsLoadingListas] = useState(false);
  const [isLoadingCandidatosLista, setIsLoadingCandidatosLista] = useState(false);
  const { showScore, showIaSearch, getDisplayName, maskValue } = useSettings();
  const { unopenedCandidatosCount, syncUnopenedFromCandidatoIds } = useUnopenedCandidatos();
  const [nuevaListaNombre, setNuevaListaNombre] = useState("");
  const [nuevaListaEmoji, setNuevaListaEmoji] = useState("📁");
  const [listaTodosActivosForm, setListaTodosActivosForm] = useState(false);
  const [editandoListaVersion, setEditandoListaVersion] = useState(null);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [openAddListaDialog, setOpenAddListaDialog] = useState(false);
  const [isCreandoLista, setIsCreandoLista] = useState(false);
  const [listaRecienCreadaId, setListaRecienCreadaId] = useState(null);
  const [listasEliminando, setListasEliminando] = useState([]);
  const [listaOrderIds, setListaOrderIds] = useState(() => {
    try {
      const raw = localStorage.getItem("candidatos-list-order-ids");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  });
  const [dragListaId, setDragListaId] = useState("");
  const [editandoListaId, setEditandoListaId] = useState(null);
  const [editandoListaNombre, setEditandoListaNombre] = useState("");
  const [revisadoresPopoverOpen, setRevisadoresPopoverOpen] = useState(false);
  const [revisadoresEdit, setRevisadoresEdit] = useState([]);
  const [revisadorDetallesEdit, setRevisadorDetallesEdit] = useState({}); // email -> { nombre?, color? }
  const [nuevoRevisadorEmail, setNuevoRevisadorEmail] = useState("");
  const [listaCarpetaTab, setListaCarpetaTab] = useState("Por revisar"); // "Por revisar" | "Descartados" (solo cuando todos_activos)
  const [batchArchiveMode, setBatchArchiveMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isArchivingBatch, setIsArchivingBatch] = useState(false);
  const [isDeletingBatch, setIsDeletingBatch] = useState(false);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [adminPasswordError, setAdminPasswordError] = useState("");
  const [emailDescartadosOpen, setEmailDescartadosOpen] = useState(false);
  const [emailDescartadosSubject, setEmailDescartadosSubject] = useState("Comunicación sobre tu candidatura");
  const [emailDescartadosTemplate, setEmailDescartadosTemplate] = useState("<p>Hola {{nombre}},</p>\n<p>Te escribimos en relación a tu candidatura.</p>\n<p>Un saludo.</p>");
  const [emailDescartadosTestTo, setEmailDescartadosTestTo] = useState("");
  const [emailDescartadosTestNombre, setEmailDescartadosTestNombre] = useState("Nombre de prueba");
  const [emailDescartadosSendingTest, setEmailDescartadosSendingTest] = useState(false);
  const [emailDescartadosSendingAll, setEmailDescartadosSendingAll] = useState(false);
  const [emailDescartadosSendingFailed, setEmailDescartadosSendingFailed] = useState(false);
  const [emailDescartadosRefreshing, setEmailDescartadosRefreshing] = useState(false);
  const [emailDescartadosConfirmSend, setEmailDescartadosConfirmSend] = useState(false);
  const [emailDescartadosPreviewTab, setEmailDescartadosPreviewTab] = useState("plantilla"); // "plantilla" | "vista-previa" | "destinatarios"
  const [destinatariosEditList, setDestinatariosEditList] = useState([]); // { nombre, email }[] editable
  const [lastSendResult, setLastSendResult] = useState(null); // { attempted: string[], failed: { email, error }[] } después de enviar

  const handleAdminSubmit = async () => {
    setAdminPasswordError("");
    try {
      await verifyAdminPassword(adminPassword);
      setAdminDialogOpen(false);
      setAdminPassword("");
      navigate("/dashboard");
    } catch (err) {
      const msg = err.response?.data?.detail ?? "Contraseña incorrecta";
      setAdminPasswordError(msg);
    }
  };

  const toggleSelectId = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const exitBatchMode = () => {
    setBatchArchiveMode(false);
    setSelectedIds([]);
  };

  const handleToggleFavorito = async (candidatoId, currentFavorito) => {
    const c = candidatos?.find((x) => x.id === candidatoId) || candidatosLista?.find((x) => x.id === candidatoId);
    const payload = { favorito: !currentFavorito };
    if (c?.version != null) payload.version = c.version;
    const newFav = !currentFavorito;
    const detalle = c?.favoritos_por_detalle ?? [];
    const userDetalle = user ? { email: user.email, nombre: user.nombre, color: user.color, avatar_variant: user.avatar_variant } : null;
    const nextDetalle = newFav
      ? (userDetalle ? [...detalle.filter((d) => d.email !== user.email), userDetalle] : detalle)
      : detalle.filter((d) => d.email !== user?.email);
    try {
      await updateCandidato(candidatoId, payload);
      setCandidatos((prev) => prev.map((x) => (x.id === candidatoId ? { ...x, favorito: newFav, favoritos_por_detalle: nextDetalle, version: (x.version ?? 0) + 1 } : x)));
      setCandidatosLista((prev) => prev.map((x) => (x.id === candidatoId ? { ...x, favorito: newFav, favoritos_por_detalle: nextDetalle, version: (x.version ?? 0) + 1 } : x)));
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response?.data?.detail || "Otro usuario ha modificado esto; recarga la página.");
        loadCandidatos();
        if (listaActiva?.id) openLista(listaActiva);
        return;
      }
      console.error(err);
      toast.error("Error al actualizar favorito");
    }
  };
  const handleBatchArchive = async () => {
    if (selectedIds.length === 0) return;
    setIsArchivingBatch(true);
    try {
      const list = candidatosOrdenados ?? candidatos ?? [];
      await Promise.all(selectedIds.map((id) => {
        const c = list.find((x) => x.id === id);
        const payload = { archivado: true };
        if (c?.version != null) payload.version = c.version;
        return updateCandidato(id, payload);
      }));
      toast.success(`${selectedIds.length} candidato(s) archivado(s)`);
      exitBatchMode();
      loadCandidatos();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response?.data?.detail || "Otro usuario ha modificado esto; recarga la página.");
        loadCandidatos();
        return;
      }
      console.error(err);
      toast.error("Error al archivar");
    } finally {
      setIsArchivingBatch(false);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`¿Borrar ${selectedIds.length} candidato(s)? Esta acción no se puede deshacer.`)) return;
    setIsDeletingBatch(true);
    try {
      await Promise.all(selectedIds.map((id) => deleteCandidato(id)));
      toast.success(`${selectedIds.length} candidato(s) borrado(s)`);
      exitBatchMode();
      loadCandidatos();
    } catch (err) {
      console.error(err);
      toast.error("Error al borrar");
    } finally {
      setIsDeletingBatch(false);
    }
  };

  const loadListas = useCallback(async () => {
    setIsLoadingListas(true);
    try {
      const data = await getListas();
      setListas((data.listas || []).map((l) => normalizarLista(l)));
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingListas(false);
    }
  }, []);

  const normalizarLista = (l) => {
    if (!l) return l;
    return {
      ...l,
      emoji: l.emoji || "📁",
      carpetas: Array.isArray(l.carpetas) ? l.carpetas : ["Por revisar", "Revisados", "Descartados"],
      revisadores: Array.isArray(l.revisadores) ? l.revisadores : [],
      revisador_detalles: l.revisador_detalles && typeof l.revisador_detalles === "object" ? l.revisador_detalles : {},
      candidatos_ids: Array.isArray(l.candidatos_ids) ? l.candidatos_ids : [],
    };
  };

  const openLista = async (lista, opts = {}) => {
    const { preserveTab = false, initialTab = null } = opts;
    const norm = normalizarLista(lista);
    setListaActiva(norm);
    if (!preserveTab) setListaCarpetaTab(initialTab || "Por revisar");
    setIsLoadingCandidatosLista(true);
    try {
      const data = await getCandidatosDeLista(lista.id);
      const list = data.candidatos || [];
      setListaActiva((prev) => (prev?.id === data.lista?.id ? normalizarLista({ ...prev, ...data.lista }) : prev));
      setCandidatosLista(list);
      syncUnopenedFromCandidatoIds(list.map((c) => c.id).filter(Boolean));
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingCandidatosLista(false);
    }
  };

  const handleSetCarpeta = async (candidatoId, carpeta) => {
    if (!listaActiva?.id) return;
    const value = carpeta === "" || !carpeta ? null : carpeta;
    const version = listaActiva.version ?? undefined;
    try {
      await setCarpetaEnLista(listaActiva.id, candidatoId, value, version);
      setCandidatosLista((prev) => prev.map((c) => (c.id === candidatoId ? { ...c, carpeta: value, revisador: (value === "Revisados" || value === "Descartados") ? user?.email : undefined } : c)));
      setListaActiva((prev) => (prev?.id === listaActiva.id ? { ...prev, version: (prev.version ?? 0) + 1 } : prev));
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response?.data?.detail || "Otro usuario ha modificado esta lista; recarga la página.");
        openLista(listaActiva);
        return;
      }
      toast.error("Error al cambiar carpeta");
    }
  };

  const handleSetRevisador = async (candidatoId, revisador) => {
    if (!listaActiva?.id) return;
    const value = revisador === "" || !revisador ? null : revisador;
    const version = listaActiva.version ?? undefined;
    try {
      await setRevisadorEnLista(listaActiva.id, candidatoId, value, version);
      setCandidatosLista((prev) => prev.map((c) => (c.id === candidatoId ? { ...c, revisador: value } : c)));
      setListaActiva((prev) => (prev?.id === listaActiva.id ? { ...prev, version: (prev.version ?? 0) + 1 } : prev));
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response?.data?.detail || "Otro usuario ha modificado esta lista; recarga la página.");
        openLista(listaActiva);
        return;
      }
      toast.error("Error al asignar revisador");
    }
  };

  const handleEmailDescartadosTest = async () => {
    const to = (emailDescartadosTestTo || "").trim();
    if (!to || !to.includes("@")) {
      toast.error("Indica un email de destino válido para la prueba");
      return;
    }
    if (!listaActiva?.id) return;
    setEmailDescartadosSendingTest(true);
    try {
      await sendEmailDescartadosTest(listaActiva.id, {
        templateHtml: emailDescartadosTemplate,
        subject: emailDescartadosSubject,
        toEmail: to,
        nombrePrueba: (emailDescartadosTestNombre || "").trim() || undefined,
      });
      toast.success("Correo de prueba enviado");
    } catch (err) {
      const detail = err.response?.data?.detail ?? err.message;
      toast.error(detail || "Error al enviar el correo de prueba");
    } finally {
      setEmailDescartadosSendingTest(false);
    }
  };

  const handleEmailDescartadosSendAll = async () => {
    if (!listaActiva?.id) return;
    const conEmail = destinatariosEditList.filter((r) => (r.email || "").trim() && (r.email || "").includes("@"));
    if (conEmail.length === 0) {
      toast.info("No hay destinatarios con email válido. Añade al menos uno en la pestaña Destinatarios.");
      return;
    }
    if (!emailDescartadosConfirmSend) {
      setEmailDescartadosConfirmSend(true);
      return;
    }
    setEmailDescartadosSendingAll(true);
    try {
      const result = await sendEmailToDescartados(listaActiva.id, {
        templateHtml: emailDescartadosTemplate,
        subject: emailDescartadosSubject,
        recipients: conEmail.map((r) => ({ nombre: (r.nombre || "").trim(), email: (r.email || "").trim() })),
      });
      const sent = result?.sent ?? 0;
      const failed = result?.failed ?? [];
      setLastSendResult({
        attempted: conEmail.map((r) => (r.email || "").trim()),
        failed: failed,
      });
      if (failed.length > 0) {
        toast.warning(`Enviados ${sent}. Fallos: ${failed.length} (${failed.map((f) => f.email).join(", ")})`);
      } else {
        toast.success(`Correo enviado a ${sent} destinatario${sent !== 1 ? "s" : ""}`);
      }
      setEmailDescartadosConfirmSend(false);
      if (listaActiva?.id) {
        updateEmailDescartadosDraft(listaActiva.id, {
          subject: emailDescartadosSubject,
          templateHtml: emailDescartadosTemplate,
          recipients: destinatariosEditList,
          lastSendResult: { attempted: conEmail.map((r) => (r.email || "").trim()), failed },
        }).then(() => {
          setListaActiva((prev) =>
            prev?.id === listaActiva.id
              ? {
                  ...prev,
                  email_descartados_draft: {
                    ...(prev.email_descartados_draft || {}),
                    subject: emailDescartadosSubject,
                    template_html: emailDescartadosTemplate,
                    recipients: destinatariosEditList,
                    last_send_result: { attempted: conEmail.map((r) => (r.email || "").trim()), failed },
                  },
                }
              : prev
          );
        }).catch(() => {});
      }
    } catch (err) {
      const detail = err.response?.data?.detail ?? err.message;
      toast.error(detail || "Error al enviar los correos");
    } finally {
      setEmailDescartadosSendingAll(false);
    }
  };

  const handleEmailDescartadosSendOnlyFailed = async () => {
    if (!listaActiva?.id || !lastSendResult?.failed?.length) return;
    const failedEmails = lastSendResult.failed.map((f) => (f.email || "").trim()).filter(Boolean);
    const recipientsOnlyFailed = failedEmails.map((email) => {
      const row = destinatariosEditList.find((r) => (r.email || "").trim().toLowerCase() === email.toLowerCase());
      return { nombre: (row?.nombre || "").trim() || email.split("@")[0], email };
    });
    setEmailDescartadosSendingFailed(true);
    try {
      const result = await sendEmailToDescartados(listaActiva.id, {
        templateHtml: emailDescartadosTemplate,
        subject: emailDescartadosSubject,
        recipients: recipientsOnlyFailed,
      });
      const sent = result?.sent ?? 0;
      const newFailed = result?.failed ?? [];
      const previousSuccess = (lastSendResult.attempted || []).filter(
        (em) => !lastSendResult.failed.some((f) => (f.email || "").trim().toLowerCase() === (em || "").trim().toLowerCase())
      );
      const retriedSuccess = failedEmails.filter(
        (em) => !newFailed.some((f) => (f.email || "").trim().toLowerCase() === (em || "").trim().toLowerCase())
      );
      const newLastSendResult = {
        attempted: [...previousSuccess, ...retriedSuccess],
        failed: newFailed,
      };
      setLastSendResult(newLastSendResult);
      if (newFailed.length > 0) {
        toast.warning(`Reenviados ${sent}. Siguen fallando: ${newFailed.length} (${newFailed.map((f) => f.email).join(", ")})`);
      } else {
        toast.success(`Reenviado correctamente a ${sent} destinatario${sent !== 1 ? "s" : ""}`);
      }
      updateEmailDescartadosDraft(listaActiva.id, {
        subject: emailDescartadosSubject,
        templateHtml: emailDescartadosTemplate,
        recipients: destinatariosEditList,
        lastSendResult: newLastSendResult,
      }).then(() => {
        setListaActiva((prev) =>
          prev?.id === listaActiva.id
            ? { ...prev, email_descartados_draft: { ...(prev.email_descartados_draft || {}), last_send_result: newLastSendResult } }
            : prev
        );
      }).catch(() => {});
    } catch (err) {
      const detail = err.response?.data?.detail ?? err.message;
      toast.error(detail || "Error al reenviar");
    } finally {
      setEmailDescartadosSendingFailed(false);
    }
  };

  const saveEmailDescartadosDraft = () => {
    if (!listaActiva?.id) return;
    updateEmailDescartadosDraft(listaActiva.id, {
      subject: emailDescartadosSubject,
      templateHtml: emailDescartadosTemplate,
      recipients: destinatariosEditList,
      lastSendResult,
    }).then(() => {
      setListaActiva((prev) =>
        prev?.id === listaActiva.id
          ? {
              ...prev,
              email_descartados_draft: {
                subject: emailDescartadosSubject,
                template_html: emailDescartadosTemplate,
                recipients: destinatariosEditList,
                last_send_result: lastSendResult || null,
              },
            }
          : prev
      );
    }).catch(() => {});
  };

  const handleActualizarDestinatarios = async () => {
    if (!listaActiva?.id) return;
    setEmailDescartadosRefreshing(true);
    try {
      const data = await getCandidatosDeLista(listaActiva.id);
      const list = data.candidatos || [];
      setListaActiva((prev) => (prev?.id === data.lista?.id ? normalizarLista({ ...prev, ...data.lista }) : prev));
      setCandidatosLista(list);
      const dest = list.filter((c) => c.carpeta === "Descartados" && (c.email || "").trim());
      const soloNombre = (n) => ((n || "").trim() ? (n || "").trim().split(/\s+/)[0] : "");
      setDestinatariosEditList(
        dest.map((c) => ({
          nombre: soloNombre(c.nombre) || (c.email || "").split("@")[0] || "",
          email: (c.email || "").trim(),
        }))
      );
      setLastSendResult(null);
    } catch (err) {
      toast.error("Error al actualizar el listado");
    } finally {
      setEmailDescartadosRefreshing(false);
    }
  };

  const handleExportDescartadosCsv = () => {
    const descartados = candidatosLista.filter((c) => c.carpeta === "Descartados");
    if (descartados.length === 0) {
      toast.info("No hay descartados para exportar");
      return;
    }
    const escapeCsv = (s) => (/[",\r\n]/.test(s) ? `"${String(s).replace(/"/g, '""')}"` : s);
    const header = "Nombre,Correo";
    const rows = descartados.map((c) => [escapeCsv(c.nombre || ""), escapeCsv(c.email || "")].join(","));
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `descartados-${listaActiva?.nombre?.replace(/[^\w\s-]/g, "") || "lista"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${descartados.length} candidato${descartados.length !== 1 ? "s" : ""} exportado${descartados.length !== 1 ? "s" : ""}`);
  };

  const handleOpenEmailDescartados = () => {
    const descartados = candidatosLista.filter((c) => c.carpeta === "Descartados");
    if (descartados.length === 0) {
      toast.info("No hay descartados para enviar correo");
      return;
    }
    setEmailDescartadosConfirmSend(false);
    setEmailDescartadosOpen(true);
  };

  const getRevisadorInicial = (candidato) => {
    if (!listaActiva?.revisador_detalles || !candidato?.revisador) return null;
    const det = listaActiva.revisador_detalles?.[candidato.revisador];
    const nombre = (det?.nombre || "").trim() || (candidato.revisador || "").split("@")[0] || "";
    const inicial = (nombre || candidato.revisador || "?").charAt(0).toUpperCase();
    return { inicial, color: det?.color || REVISADOR_COLORS[0] };
  };

  const openRevisadoresPopover = () => {
    setRevisadoresEdit([...(listaActiva?.revisadores || [])]);
    setRevisadorDetallesEdit({ ...(listaActiva?.revisador_detalles || {}) });
    setNuevoRevisadorEmail("");
    setRevisadoresPopoverOpen(true);
  };

  const handleAddRevisador = () => {
    const email = nuevoRevisadorEmail.trim().toLowerCase();
    if (!email) return;
    if (revisadoresEdit.includes(email)) return;
    setRevisadoresEdit((prev) => [...prev, email]);
    setRevisadorDetallesEdit((prev) => ({
      ...prev,
      [email]: {
        nombre: (prev[email]?.nombre) || email.split("@")[0] || email,
        color: (prev[email]?.color) || REVISADOR_COLORS[0],
      },
    }));
    setNuevoRevisadorEmail("");
  };

  const handleRemoveRevisador = (email) => {
    setRevisadoresEdit((prev) => prev.filter((e) => e !== email));
    setRevisadorDetallesEdit((prev) => {
      const next = { ...prev };
      delete next[email];
      return next;
    });
  };

  const handleRevisadorDetalle = (email, field, value) => {
    setRevisadorDetallesEdit((prev) => ({
      ...prev,
      [email]: { ...(prev[email] || {}), [field]: value },
    }));
  };

  const handleSaveRevisadores = async () => {
    if (!listaActiva?.id) return;
    const detalles = {};
    revisadoresEdit.forEach((e) => {
      if (revisadorDetallesEdit[e]) detalles[e] = revisadorDetallesEdit[e];
    });
    const payload = { revisadores: revisadoresEdit, revisador_detalles: detalles };
    if (listaActiva.version != null) payload.version = listaActiva.version;
    try {
      await updateLista(listaActiva.id, payload);
      setListaActiva((prev) =>
        prev?.id === listaActiva.id
          ? { ...prev, revisadores: revisadoresEdit, revisador_detalles: detalles, version: (prev.version ?? 0) + 1 }
          : prev
      );
      setRevisadoresPopoverOpen(false);
      toast.success("Revisadores actualizados");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response?.data?.detail || "Otro usuario ha modificado esta lista; recarga la página.");
        openLista(listaActiva);
        return;
      }
      toast.error("Error al guardar revisadores");
    }
  };

  const handleCrearLista = async () => {
    if (!nuevaListaNombre.trim()) return;
    setIsCreandoLista(true);
    try {
      if (editandoListaId) {
        const payload = {
          nombre: nuevaListaNombre.trim(),
          emoji: nuevaListaEmoji || "📁",
          todos_activos: !!listaTodosActivosForm,
        };
        if (editandoListaVersion != null) payload.version = editandoListaVersion;
        await updateLista(editandoListaId, payload);
        toast.success("Lista actualizada");
      } else {
        const data = await createLista(nuevaListaNombre.trim(), {
          emoji: nuevaListaEmoji || "📁",
          todos_activos: !!listaTodosActivosForm,
        });
        const newId = data?.id ?? null;
        if (newId != null) {
          setListaRecienCreadaId(newId);
          setTimeout(() => setListaRecienCreadaId(null), 350);
        }
        toast.success("Lista creada");
      }
      setEditandoListaId(null);
      setEditandoListaVersion(null);
      setNuevaListaNombre("");
      setNuevaListaEmoji("📁");
      setListaTodosActivosForm(false);
      setOpenAddListaDialog(false);
      setEmojiPickerOpen(false);
      await loadListas();
      if (listaActiva?.id && editandoListaId === listaActiva.id) {
        await openLista({ ...listaActiva, id: listaActiva.id }, { preserveTab: true });
      }
    } catch (err) {
      console.error(err);
      toast.error(editandoListaId ? "Error al actualizar la lista" : "Error al crear la lista");
    } finally {
      setIsCreandoLista(false);
    }
  };

  const handleEliminarLista = async (listaId) => {
    if (!window.confirm("¿Eliminar esta lista? Los candidatos no se borrarán.")) return;
    setListasEliminando((prev) => [...prev, listaId]);
    const duration = 250;
    setTimeout(async () => {
      try {
        await deleteLista(listaId);
        if (listaActiva?.id === listaId) setListaActiva(null);
        await loadListas();
      } catch (err) {
        toast.error("Error al eliminar la lista");
      } finally {
        setListasEliminando((prev) => prev.filter((id) => id !== listaId));
      }
    }, duration);
  };

  const handleRenombrarLista = async (listaId) => {
    if (!editandoListaNombre.trim()) return;
    const lista = listas?.find((l) => l.id === listaId) || (listaActiva?.id === listaId ? listaActiva : null);
    const payload = { nombre: editandoListaNombre.trim() };
    if (lista?.version != null) payload.version = lista.version;
    try {
      await updateLista(listaId, payload);
      setEditandoListaId(null);
      setEditandoListaNombre("");
      await loadListas();
      if (listaActiva?.id === listaId) {
        setListaActiva((prev) => ({ ...prev, nombre: editandoListaNombre.trim(), version: (prev?.version ?? 0) + 1 }));
      }
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response?.data?.detail || "Otro usuario ha modificado esta lista; recarga la página.");
        loadListas();
        if (listaActiva?.id === listaId) openLista({ ...listaActiva, id: listaId });
        return;
      }
      toast.error("Error al renombrar la lista");
    }
  };

  const candidatosOrdenados = useMemo(() => {
    const list = Array.isArray(candidatos) ? [...candidatos] : [];
    if (list.length === 0) return list;
    if (ordenarPor === "nuevos") return list.sort((a, b) => (new Date(b.fecha_creacion || 0) - new Date(a.fecha_creacion || 0)));
    if (ordenarPor === "viejos") return list.sort((a, b) => (new Date(a.fecha_creacion || 0) - new Date(b.fecha_creacion || 0)));
    if (ordenarPor === "puntuacion_mayor") return list.sort((a, b) => (b.score_agencia ?? 0) - (a.score_agencia ?? 0));
    if (ordenarPor === "puntuacion_menor") return list.sort((a, b) => (a.score_agencia ?? 0) - (b.score_agencia ?? 0));
    if (ordenarPor === "alfabetico") return list.sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es", { sensitivity: "base" }));
    return list;
  }, [candidatos, ordenarPor]);

  const orderedListas = useMemo(() => {
    if (!listas?.length) return [];
    if (!listaOrderIds?.length) return listas;
    const byId = new Map(listas.map((l) => [l.id, l]));
    const ordered = [];
    for (const id of listaOrderIds) {
      if (byId.has(id)) ordered.push(byId.get(id));
    }
    for (const l of listas) {
      if (!listaOrderIds.includes(l.id)) ordered.push(l);
    }
    return ordered;
  }, [listas, listaOrderIds]);

  const persistListaOrder = useCallback((ids) => {
    setListaOrderIds(ids);
    try {
      localStorage.setItem("candidatos-list-order-ids", JSON.stringify(ids));
    } catch (_) {}
  }, []);

  const reorderListas = useCallback((fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    const current = [...(listaOrderIds?.length ? listaOrderIds : listas.map((l) => l.id))];
    const fromIdx = current.indexOf(fromId);
    const toIdx = current.indexOf(toId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = current.splice(fromIdx, 1);
    current.splice(toIdx, 0, moved);
    persistListaOrder(current);
  }, [listaOrderIds, listas, persistListaOrder]);

  useEffect(() => {
    if (!listas?.length) return;
    const listIds = listas.map((l) => l.id);
    if (!listaOrderIds.length) {
      persistListaOrder(listIds);
      return;
    }
    const cleaned = listaOrderIds.filter((id) => listIds.includes(id));
    const missing = listIds.filter((id) => !cleaned.includes(id));
    if (cleaned.length !== listaOrderIds.length || missing.length > 0) {
      persistListaOrder([...cleaned, ...missing]);
    }
  }, [listas, listaOrderIds, persistListaOrder]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/login");
      return;
    }
  }, [navigate]);

  // Redirigir si el usuario no tiene permiso para Candidatos
  useEffect(() => {
    if (!user) return;
    if (user.permissions?.candidatos === false) {
      if (user.permissions?.formularios !== false) navigate("/formularios", { replace: true });
      else if (user.permissions?.revisiones !== false) navigate("/revisiones", { replace: true });
      else if (user.permissions?.onboarding !== false) navigate("/onboarding", { replace: true });
      else if (user.permissions?.proveedores !== false) navigate("/proveedores", { replace: true });
      else if (user.permissions?.archivos !== false) navigate("/brutalfiles", { replace: true });
      else if (user.permissions?.correos !== false) navigate("/correos", { replace: true });
      else if (user.permissions?.metricas) navigate("/metricas", { replace: true });
      else navigate("/", { replace: true });
    }
  }, [user, navigate]);

  // Load listas on mount
  useEffect(() => { loadListas(); }, [loadListas]);

  // Sincronizar filtros con la URL (para que al volver atrás desde un candidato se mantengan)
  useEffect(() => {
    const params = {};
    if (busqueda) params.busqueda = busqueda;
    if (segmento && segmento !== "all") params.segmento = segmento;
    if (nivel && nivel !== "all") params.nivel = nivel;
    if (experiencia[0] > 0) params.exp_min = String(experiencia[0]);
    if (experiencia[1] < 20) params.exp_max = String(experiencia[1]);
    if (minScore > 0) params.min_score = String(minScore);
    if (rangoSalarial[0] > 0) params.sal_min = String(rangoSalarial[0]);
    if (rangoSalarial[1] < 100) params.sal_max = String(rangoSalarial[1]);
    if (ubicacion && ubicacion !== "all") params.ubicacion = ubicacion;
    if (situacionActual && situacionActual !== "all") params.situacion = situacionActual;
    if (carnetConducir) params.carnet = "1";
    if (vehiculoPropio) params.vehiculo = "1";
    if (ordenarPor && ordenarPor !== "nuevos") params.ordenar = ordenarPor;
    const current = Object.fromEntries([...searchParams.entries()]);
    if (JSON.stringify(current) !== JSON.stringify(params)) {
      setSearchParams(params, { replace: true });
    }
  }, [busqueda, segmento, nivel, experiencia, minScore, rangoSalarial, ubicacion, situacionActual, carnetConducir, vehiculoPropio, ordenarPor, setSearchParams, searchParams]);

  // Abrir lista al volver desde la ficha de un candidato ("Volver a la lista")
  useEffect(() => {
    const id = location.state?.openListaId;
    const tab = location.state?.fromListaCarpetaTab;
    if (!id || listas.length === 0) return;
    const lista = listas.find((l) => l.id === id);
    if (lista) {
      setTabListas(true);
      setTabFavoritos(false);
      setTabActivos(true);
      openLista(lista, { initialTab: tab || "Por revisar" });
    }
    navigate("/candidatos", { replace: true, state: {} });
  }, [location.state?.openListaId, location.state?.fromListaCarpetaTab, listas, navigate]);

  // Restaurar búsqueda IA al volver atrás (p. ej. desde la ficha de un candidato)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("candidatos_ia_search");
      if (raw) {
        const { query, results } = JSON.parse(raw);
        if (query) setIaQuery(query);
        if (results) setIaResults(results);
      }
    } catch (_) {}
  }, []);

  // Load ubicaciones on mount
  useEffect(() => {
    const loadUbicaciones = async () => {
      try {
        const data = await getUbicaciones();
        setUbicaciones(data.ubicaciones || []);
      } catch (error) {
        console.error("Error loading ubicaciones:", error);
      }
    };
    loadUbicaciones();
  }, [candidatos]); // Reload when candidatos change

  const loadCandidatos = useCallback(async () => {
    setIsLoading(true);
    try {
      const filters = {};
      if (busqueda) filters.busqueda = busqueda;
      if (segmento && segmento !== "all") filters.segmento = segmento;
      if (nivel && nivel !== "all") filters.nivel = nivel;
      if (experiencia[0] > 0) filters.minExperiencia = experiencia[0];
      if (experiencia[1] < 20) filters.maxExperiencia = experiencia[1];
      if (minScore > 0) filters.minScore = minScore;
      if (rangoSalarial[0] > 0 || rangoSalarial[1] < 100) {
        filters.minRangoSalarial = rangoSalarial[0];
        filters.maxRangoSalarial = rangoSalarial[1];
      }
      if (ubicacion && ubicacion !== "all") filters.provincia = ubicacion;
      if (situacionActual && situacionActual !== "all") filters.situacionActual = situacionActual;
      if (carnetConducir) filters.carnetConducir = true;
      if (vehiculoPropio) filters.vehiculoPropio = true;
      if (tabFavoritos) {
        filters.favorito = true;
      } else if (!tabActivos) {
        filters.archivado = true;
      }

      const data = await getCandidatos(filters);
      const list = Array.isArray(data.candidatos) ? data.candidatos : [];
      setCandidatos(list);
      setTotal(data.total ?? 0);
      syncUnopenedFromCandidatoIds(list.map((c) => c.id).filter(Boolean));
    } catch (error) {
      console.error(error);
      toast.error("Error al cargar candidatos");
    } finally {
      setIsLoading(false);
    }
  }, [busqueda, segmento, nivel, experiencia, minScore, rangoSalarial, ubicacion, situacionActual, carnetConducir, vehiculoPropio, tabActivos, tabFavoritos, syncUnopenedFromCandidatoIds]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadCandidatos();
    }, 300);
    return () => clearTimeout(timer);
  }, [loadCandidatos]);

  useEffect(() => {
    if (!isFiltersClosing) return;
    const t = setTimeout(() => {
      setShowFilters(false);
      setIsFiltersClosing(false);
    }, 250);
    return () => clearTimeout(t);
  }, [isFiltersClosing]);

  useEffect(() => {
    const handler = (e) => {
      const { candidatoId, favoritos_por_detalle } = e.detail || {};
      if (!candidatoId || !Array.isArray(favoritos_por_detalle)) return;
      const isFav = user && favoritos_por_detalle.some((u) => u.email === user.email);
      setCandidatos((prev) => prev.map((c) => (c.id === candidatoId ? { ...c, favoritos_por_detalle, favorito: isFav } : c)));
      setCandidatosLista((prev) => prev.map((c) => (c.id === candidatoId ? { ...c, favoritos_por_detalle, favorito: isFav } : c)));
    };
    window.addEventListener("candidatos-favorito-update", handler);
    return () => window.removeEventListener("candidatos-favorito-update", handler);
  }, [user?.email]);

  useEffect(() => {
    const handler = (e) => {
      const listaId = e.detail?.listaId;
      if (!listaId || listaActiva?.id !== listaId) return;
      openLista(listaActiva, { preserveTab: true });
    };
    window.addEventListener("lista-updated", handler);
    return () => window.removeEventListener("lista-updated", handler);
  }, [listaActiva]);

  const interestWeightsRef = useRef({ loadCandidatos, openLista, listaActiva });
  interestWeightsRef.current = { loadCandidatos, openLista, listaActiva };
  const refreshScores = () => {
    const { loadCandidatos: load, openLista: open, listaActiva: lista } = interestWeightsRef.current;
    load();
    if (lista?.id) open(lista, { preserveTab: true });
  };
  useEffect(() => {
    const handler = () => refreshScores();
    window.addEventListener(INTEREST_WEIGHTS_UPDATED_EVENT, handler);
    const onStorage = (e) => {
      if (e.key === INTEREST_WEIGHTS_STORAGE_KEY) refreshScores();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(INTEREST_WEIGHTS_UPDATED_EVENT, handler);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const emailDescartadosOpenRef = useRef(false);
  useEffect(() => {
    if (!emailDescartadosOpen) {
      emailDescartadosOpenRef.current = false;
      return;
    }
    if (emailDescartadosOpenRef.current) {
      return;
    }
    emailDescartadosOpenRef.current = true;
    const draft = listaActiva?.email_descartados_draft;
    const hasDraftContent = draft && (draft.subject != null || draft.template_html != null);
    const hasDraftRecipients = draft && Array.isArray(draft.recipients) && draft.recipients.length > 0;
    if (hasDraftContent) {
      setEmailDescartadosSubject(draft.subject ?? "Comunicación sobre tu candidatura");
      setEmailDescartadosTemplate(draft.template_html ?? "<p>Hola {{nombre}},</p>\n<p>Te escribimos en relación a tu candidatura.</p>\n<p>Un saludo.</p>");
    }
    if (hasDraftRecipients) {
      setDestinatariosEditList(draft.recipients.map((r) => ({ nombre: r.nombre ?? "", email: r.email ?? "" })));
      const lsr = draft.last_send_result;
      setLastSendResult(lsr && ((Array.isArray(lsr.attempted) && lsr.attempted.length) || (Array.isArray(lsr.failed) && lsr.failed.length)) ? { attempted: lsr.attempted || [], failed: lsr.failed || [] } : null);
    } else {
      const dest = candidatosLista.filter((c) => c.carpeta === "Descartados" && (c.email || "").trim());
      const soloNombre = (n) => ((n || "").trim() ? (n || "").trim().split(/\s+/)[0] : "");
      setDestinatariosEditList(
        dest.map((c) => ({
          nombre: soloNombre(c.nombre) || (c.email || "").split("@")[0] || "",
          email: (c.email || "").trim(),
        }))
      );
      setLastSendResult(null);
    }
  }, [emailDescartadosOpen, listaActiva?.id, listaActiva?.email_descartados_draft, candidatosLista]);

  const clearFilters = () => {
    setBusqueda("");
    setSegmento("");
    setNivel("");
    setExperiencia([0, 20]);
    setMinScore(0);
    setRangoSalarial([0, 100]);
    setUbicacion("");
    setSituacionActual("all");
    setCarnetConducir(false);
    setVehiculoPropio(false);
  };

  const handleIASearch = async (e) => {
    e.preventDefault();
    if (!iaQuery.trim()) return;
    
    setIsSearchingIA(true);
    setIaResults(null);
    
    try {
      const results = await busquedaIA(iaQuery.trim());
      setIaResults(results);
      try {
        sessionStorage.setItem(
          "candidatos_ia_search",
          JSON.stringify({ query: iaQuery.trim(), results })
        );
      } catch (_) {}
    } catch (error) {
      console.error(error);
      toast.error("Error al realizar la búsqueda");
    } finally {
      setIsSearchingIA(false);
    }
  };

  const clearIASearch = () => {
    setIaQuery("");
    setIaResults(null);
    try {
      sessionStorage.removeItem("candidatos_ia_search");
    } catch (_) {}
  };

  const hasActiveFilters = busqueda || (segmento && segmento !== "all") || (nivel && nivel !== "all") || experiencia[0] > 0 || experiencia[1] < 20 || minScore > 0 || rangoSalarial[0] > 0 || rangoSalarial[1] < 100 || (ubicacion && ubicacion !== "all") || (situacionActual && situacionActual !== "all") || carnetConducir || vehiculoPropio;

  /** Parsea rango_salarial y devuelve maxK en miles (para filtro igual que en tarjeta). */
  const rangoSalarialMaxK = useCallback((rangoStr) => {
    if (!rangoStr || typeof rangoStr !== "string") return null;
    const cleaned = rangoStr.trim().replace(/k|€/gi, "").replace(/\./g, "").replace(/,/g, ".");
    const nums = cleaned.split(/[-–—\s]+/).map((p) => parseInt(p.trim(), 10)).filter((n) => !Number.isNaN(n) && n > 0);
    if (nums.length === 0) return null;
    let maxK = Math.max(...nums);
    if (maxK > 99) maxK = Math.round(maxK / 1000);
    return maxK;
  }, []);

  /** Lista de candidatos de la lista activa filtrada por los filtros globales (busqueda, segmento, etc.). */
  const candidatosListaConFiltros = useMemo(() => {
    const list = Array.isArray(candidatosLista) ? candidatosLista : [];
    if (list.length === 0) return list;
    const q = (busqueda || "").trim().toLowerCase();
    const hasBusqueda = q.length > 0;
    const hasSegmento = segmento && segmento !== "all";
    const hasNivel = nivel && nivel !== "all";
    const hasExperiencia = experiencia[0] > 0 || experiencia[1] < 20;
    const hasMinScore = minScore > 0;
    const hasRango = rangoSalarial[0] > 0 || rangoSalarial[1] < 100;
    const hasUbicacion = ubicacion && ubicacion !== "all";
    const hasSituacion = situacionActual && situacionActual !== "all";
    const rMin = rangoSalarial[0];
    const rMax = rangoSalarial[1] === 100 ? 999 : rangoSalarial[1];
    return list.filter((c) => {
      if (hasBusqueda) {
        const text = [c.nombre, c.rol_principal, (c.segmentos || []).join(" "), (c.habilidades || []).join(" ")].filter(Boolean).join(" ").toLowerCase();
        const terms = q.split(/\s+/).filter((t) => t.length >= 2);
        const match = terms.length === 0 ? text.includes(q) : terms.every((t) => text.includes(t));
        if (!match) return false;
      }
      if (hasSegmento && (!c.segmentos || !c.segmentos.includes(segmento))) return false;
      if (hasNivel && c.nivel !== nivel) return false;
      if (hasExperiencia) {
        const exp = c.años_experiencia ?? 0;
        if (exp < experiencia[0] || (experiencia[1] < 20 && exp > experiencia[1])) return false;
      }
      if (hasMinScore && (c.score_agencia ?? 0) < minScore) return false;
      if (hasRango) {
        const maxK = rangoSalarialMaxK(c.rango_salarial);
        if (maxK == null || maxK < rMin || maxK > rMax) return false;
      }
      if (hasUbicacion) {
        const prov = (c.provincia || "").toLowerCase();
        const ciu = (c.ciudad || "").toLowerCase();
        const u = ubicacion.toLowerCase();
        if (!prov.includes(u) && !ciu.includes(u)) return false;
      }
      if (hasSituacion && (c.situacion_actual || "").toLowerCase() !== situacionActual.toLowerCase()) return false;
      if (carnetConducir && !c.carnet_conducir) return false;
      if (vehiculoPropio && !c.vehiculo_propio) return false;
      return true;
    });
  }, [candidatosLista, busqueda, segmento, nivel, experiencia, minScore, rangoSalarial, ubicacion, situacionActual, carnetConducir, vehiculoPropio, rangoSalarialMaxK]);

  const { comparisonIds, addToComparison, removeFromComparison, maxComparison } = useComparison();

  function CandidatoCardWithCompare({ candidato, revisadorInicial, onDescartar, onRevisado, onRestaurar, ...cardProps }) {
    const inCompare = comparisonIds.includes(candidato.id);
    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div className="h-full [&>div]:h-full">
            <CandidatoCard candidato={candidato} showScore={showScore} revisadorInicial={revisadorInicial} currentUser={user} maskValue={maskValue} {...cardProps} />
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-52">
          {inCompare ? (
            <ContextMenuItem onSelect={() => removeFromComparison(candidato.id)}>
              Quitar de comparativa
            </ContextMenuItem>
          ) : (
            <ContextMenuItem
              onSelect={() => {
                if (comparisonIds.length >= maxComparison) {
                  toast.error("Máximo 3 perfiles para comparar");
                } else {
                  addToComparison(candidato.id);
                  toast.success("Añadido a comparativa");
                }
              }}
            >
              Añadir a comparativa
            </ContextMenuItem>
          )}
          {onRevisado && (
            <ContextMenuItem onSelect={onRevisado}>
              Marcar como revisado
            </ContextMenuItem>
          )}
          {onDescartar && (
            <ContextMenuItem onSelect={onDescartar} className="text-destructive focus:text-destructive">
              Descartar
            </ContextMenuItem>
          )}
          {onRestaurar && (
            <ContextMenuItem onSelect={onRestaurar}>
              Restaurar
            </ContextMenuItem>
          )}
        </ContextMenuContent>
      </ContextMenu>
    );
  }

  const sidebarBody = (
    <>
          <div className="p-4 shrink-0 border-b border-gray-200">
            <Button
              onClick={() => {
                setEditandoListaId(null);
                setEditandoListaVersion(null);
                setNuevaListaNombre("");
                setNuevaListaEmoji("📁");
                setListaTodosActivosForm(false);
                setMobileViewsOpen(false);
                setOpenAddListaDialog(true);
              }}
              className="w-full gap-2"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
              Añadir lista
            </Button>
          </div>
          <div className="p-4 flex-1 overflow-y-auto space-y-3">
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              variant={tabActivos && !tabListas && !tabFavoritos ? "secondary" : "ghost"}
              className="justify-start"
              onClick={() => { setTabActivos(true); setTabFavoritos(false); setTabListas(false); setListaActiva(null); exitBatchMode(); }}
              data-testid="tab-activos"
            >
              <span>Activos</span>
              {unopenedCandidatosCount > 0 && (
                <span className="ml-auto inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-medium">
                  {unopenedCandidatosCount}
                </span>
              )}
            </Button>
            <Button
              type="button"
              variant={tabFavoritos && !tabListas ? "secondary" : "ghost"}
              className="justify-start"
              onClick={() => { setTabFavoritos(true); setTabActivos(true); setTabListas(false); setListaActiva(null); exitBatchMode(); }}
              data-testid="tab-favoritos"
            >
              Favoritos
            </Button>
            <Button
              type="button"
              variant={!tabActivos && !tabFavoritos && !tabListas ? "secondary" : "ghost"}
              className="justify-start"
              onClick={() => { setTabActivos(false); setTabFavoritos(false); setTabListas(false); setListaActiva(null); exitBatchMode(); }}
              data-testid="tab-archivados"
            >
              Archivados
            </Button>
          </div>
          <div className="pt-2 space-y-2">
            {isLoadingListas ? (
              <div className="flex items-center justify-center py-6 text-muted-foreground text-sm">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Cargando listas...
              </div>
            ) : listas.length === 0 ? (
              <p className="text-xs text-muted-foreground px-1">No hay listas creadas</p>
            ) : (
              orderedListas.map((lista) => {
                const isActive = tabListas && listaActiva?.id === lista.id;
                const count = lista.candidatos_count ?? (lista.candidatos_ids || []).length;
                return (
                  <Card
                    key={lista.id}
                    className={`shadow-none hover:bg-muted/50 ${isActive ? "bg-muted/40" : ""}`}
                    draggable
                    onDragStart={() => setDragListaId(lista.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      reorderListas(dragListaId, lista.id);
                      setDragListaId("");
                    }}
                  >
                    <CardContent className="p-3 py-3 flex gap-3 items-center">
                      <button
                        type="button"
                        className="flex-1 min-w-0 text-left flex items-center gap-3"
                        onClick={() => {
                          setTabListas(true);
                          setTabFavoritos(false);
                          setTabActivos(true);
                          exitBatchMode();
                          openLista(lista);
                        }}
                      >
                        <div className="w-11 h-11 shrink-0 rounded-lg bg-muted/50 flex items-center justify-center">
                          <span className="text-2xl leading-none select-none inline-block" style={{ textShadow: "rgba(0, 0, 0, 0.3) 0px 1px 2px" }}>
                            {lista.emoji || "📁"}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                          <p className="text-sm font-medium truncate">{lista.nombre}</p>
                          <p className="text-xs text-muted-foreground">{count} candidato{count !== 1 ? "s" : ""}</p>
                        </div>
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onSelect={(e) => {
                              e.preventDefault();
                              setEditandoListaId(lista.id);
                              setNuevaListaNombre(lista.nombre || "");
                              setNuevaListaEmoji(lista.emoji || "📁");
                              setListaTodosActivosForm(!!lista.todos_activos);
                              setEditandoListaVersion(lista.version ?? null);
                              setMobileViewsOpen(false);
                              setOpenAddListaDialog(true);
                            }}
                          >
                            Editar lista
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onSelect={(e) => {
                              e.preventDefault();
                              handleEliminarLista(lista.id);
                            }}
                          >
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
          {comparisonIds.length > 0 && (
            <Button
              variant="outline"
              onClick={() => navigate("/comparar")}
              className="w-full gap-2 justify-start"
              data-testid="comparar-btn"
            >
              <LayoutGrid className="w-4 h-4" strokeWidth={1.5} />
              Comparar ({comparisonIds.length})
            </Button>
          )}
          </div>
    </>
  );

  const vistaActualLabel = tabListas && listaActiva
    ? `${listaActiva.emoji || "📁"} ${listaActiva.nombre}`
    : tabListas ? "Listas" : tabFavoritos ? "Favoritos" : tabActivos ? "Activos" : "Archivados";

  return (
    <div className="h-dvh min-w-0 flex flex-col overflow-hidden">
      <AppMainHeader title="Candidatos" titleSuffix={` / ${total}`} />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <aside className="hidden md:flex w-80 shrink-0 border-r border-gray-300 bg-background flex-col overflow-hidden">
          {sidebarBody}
        </aside>
        <div className="flex-1 min-w-0 overflow-y-auto mobile-nav-scroll">

      {/* Filters: justo debajo del header, empuja tabs y ordenación */}
      {(showFilters || isFiltersClosing) && !iaResults && (
        <div className={`border-b border-input bg-white ${isFiltersClosing ? "animate-fade-out" : "animate-fade-in"}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
            <div className="flex flex-wrap gap-4 items-end mt-2">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs text-muted-foreground mb-1.5 block">Buscar</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  <Input
                    placeholder="Nombre, rol o habilidad..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    className="pl-9 h-9"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <div className="w-48">
                <label className="text-xs text-muted-foreground mb-1.5 block">Segmento</label>
                <Select value={segmento} onValueChange={setSegmento}>
                  <SelectTrigger className="h-9" data-testid="segmento-select">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent className="z-[110]">
                    <SelectItem value="all">Todos</SelectItem>
                    {[...SEGMENTOS].sort((a, b) => a.localeCompare(b, "es")).map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-32">
                <label className="text-xs text-muted-foreground mb-1.5 block">Nivel</label>
                <Select value={nivel} onValueChange={setNivel}>
                  <SelectTrigger className="h-9" data-testid="nivel-select">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent className="z-[110]">
                    <SelectItem value="all">Todos</SelectItem>
                    {NIVELES.map((n) => (
                      <SelectItem key={n} value={n}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-44">
                <label className="text-xs text-muted-foreground mb-1.5 block">Ubicación</label>
                <Select value={ubicacion} onValueChange={setUbicacion}>
                  <SelectTrigger className="h-9" data-testid="ubicacion-select">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent className="z-[110]">
                    <SelectItem value="all">Todas</SelectItem>
                    {ubicaciones.map((u) => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-40">
                <label className="text-xs text-muted-foreground mb-1.5 block">Situación</label>
                <Select value={situacionActual} onValueChange={setSituacionActual}>
                  <SelectTrigger className="h-9" data-testid="situacion-select">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent className="z-[110]">
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="freelance">Freelance</SelectItem>
                    <SelectItem value="asalariado">Asalariado/a</SelectItem>
                    <SelectItem value="estudiante">Estudiante</SelectItem>
                    <SelectItem value="ninguno">Ninguno</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-4 h-9">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox checked={carnetConducir} onCheckedChange={setCarnetConducir} data-testid="carnet-checkbox" />
                  <span className="text-muted-foreground">Carnet</span>
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox checked={vehiculoPropio} onCheckedChange={setVehiculoPropio} data-testid="vehiculo-checkbox" />
                  <span className="text-muted-foreground">Vehículo</span>
                </label>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 items-end mt-4 pt-4 border-t">
              <div className="w-44 shrink-0">
                <label className="text-xs text-muted-foreground mb-1.5 block">Experiencia: {experiencia[0]} - {experiencia[1] === 20 ? "20+" : experiencia[1]} años</label>
                <Slider value={experiencia} onValueChange={setExperiencia} min={0} max={20} step={1} className="py-2" data-testid="experiencia-slider" />
              </div>
              <div className="w-44 shrink-0">
                <label className="text-xs text-muted-foreground mb-1.5 block">Score mínimo: {minScore}</label>
                <Slider value={[minScore]} onValueChange={([v]) => setMinScore(v)} min={0} max={100} step={5} className="py-2" data-testid="score-slider" />
              </div>
              <div className="w-44 shrink-0">
                <label className="text-xs text-muted-foreground mb-1.5 block">Rango salarial: {rangoSalarial[0]}k - {rangoSalarial[1] === 100 ? "100+" : `${rangoSalarial[1]}k`} €</label>
                <Slider value={rangoSalarial} onValueChange={setRangoSalarial} min={0} max={100} step={5} className="py-2" data-testid="rango-salarial-slider" />
              </div>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters} className="ml-auto shrink-0 gap-1.5 h-9 font-medium" data-testid="clear-filters-btn">
                  <X className="w-4 h-4" strokeWidth={1.5} />
                  Limpiar filtros
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Acciones de vista */}
      {!iaResults && (
        <div className="border-b border-transparent bg-muted/20 pt-4 pb-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-wrap md:flex-nowrap items-center justify-between gap-2 md:gap-4">
            <button
              type="button"
              onClick={() => setMobileViewsOpen(true)}
              className="md:hidden flex h-10 min-w-0 max-w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm font-medium"
              aria-label="Cambiar vista"
            >
              <span className="truncate">{vistaActualLabel}</span>
              <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
            </button>
            <div className="order-last md:order-none w-full md:w-auto flex items-center gap-2 flex-wrap">
              {batchArchiveMode && ((tabActivos && !tabListas && !tabFavoritos) || (tabListas && !!listaActiva)) ? (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-9 text-xs"
                    disabled={selectedIds.length === 0 || isArchivingBatch}
                    onClick={handleBatchArchive}
                    data-testid="batch-archive-btn"
                  >
                    {isArchivingBatch ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" strokeWidth={1.5} /> : <Archive className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />}
                    Archivar {selectedIds.length > 0 ? `(${selectedIds.length})` : ""}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-9 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                    disabled={selectedIds.length === 0 || isDeletingBatch}
                    onClick={handleBatchDelete}
                    data-testid="batch-delete-btn"
                  >
                    {isDeletingBatch ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" strokeWidth={1.5} /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />}
                    Borrar {selectedIds.length > 0 ? `(${selectedIds.length})` : ""}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-9 text-xs"
                    onClick={exitBatchMode}
                    data-testid="batch-archive-cancel"
                  >
                    Cancelar
                  </Button>
                </div>
              ) : (
                <>
                  {tabListas && !!listaActiva && (
                    <Tabs value={listaCarpetaTab} onValueChange={setListaCarpetaTab} className="w-full sm:w-auto order-last sm:order-none">
                      <TabsList className="h-10 sm:h-9 w-full sm:w-auto grid grid-cols-3 sm:flex rounded-md border border-input bg-background p-0.5">
                        <TabsTrigger value="Por revisar" className="h-full px-1 sm:px-3 text-xs sm:text-sm font-medium rounded-[5px] text-muted-foreground/50 hover:text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">Por revisar {candidatosListaConFiltros.filter((c) => !c.carpeta || c.carpeta === "Por revisar").length}</TabsTrigger>
                        <TabsTrigger value="Revisados" className="h-full px-1 sm:px-3 text-xs sm:text-sm font-medium rounded-[5px] text-muted-foreground/50 hover:text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">Revisados {candidatosListaConFiltros.filter((c) => c.carpeta === "Revisados").length}</TabsTrigger>
                        <TabsTrigger value="Descartados" className="h-full px-1 sm:px-3 text-xs sm:text-sm font-medium rounded-[5px] text-muted-foreground/50 hover:text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">Descartados {candidatosListaConFiltros.filter((c) => c.carpeta === "Descartados").length}</TabsTrigger>
                      </TabsList>
                    </Tabs>
                  )}
                  {(tabListas || (tabActivos && !tabListas && !tabFavoritos)) && (
                    <button
                      type="button"
                      onClick={() => { if (showFilters) { setIsFiltersClosing(true); } else { setShowFilters(true); } }}
                      className={`flex h-9 w-9 items-center justify-center rounded-md border border-input transition-colors ${showFilters ? "bg-muted text-foreground" : "bg-background text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                      title="Filtros"
                      aria-label="Filtros"
                      data-testid="toggle-filters-btn"
                    >
                      <Filter className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  )}
                  {((tabActivos && !tabListas && !tabFavoritos) || (tabListas && !!listaActiva)) && (
                    <>
                      <button
                        type="button"
                        onClick={() => setBatchArchiveMode(true)}
                        className="flex h-9 w-9 items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        title="Archivar varios"
                        aria-label="Archivar varios"
                        data-testid="batch-archive-icon"
                      >
                        <Archive className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setBatchArchiveMode(true)}
                        className="flex h-9 w-9 items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        title="Borrar varios"
                        aria-label="Borrar varios"
                        data-testid="batch-delete-icon"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
            {tabListas && !!listaActiva ? (
              <div className="flex items-center gap-2 ml-auto">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 shrink-0 bg-white"
                      onClick={handleExportDescartadosCsv}
                      aria-label="Exportar emails de descartados"
                    >
                      <Download className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Exportar emails de descartados (CSV)</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 shrink-0 bg-white"
                      onClick={handleOpenEmailDescartados}
                      aria-label="Enviar correo a descartados"
                    >
                      <Mail className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Enviar correo a descartados</TooltipContent>
                </Tooltip>
              </div>
            ) : !tabListas ? (
              <div className="flex h-9 items-center rounded-md border border-input bg-background">
                <Select value={ordenarPor} onValueChange={setOrdenarPor}>
                  <SelectTrigger
                    className="h-full min-h-0 w-9 md:w-[160px] justify-center md:justify-between text-xs border-0 bg-transparent shadow-none focus:ring-0 text-muted-foreground hover:text-foreground [&>span]:truncate py-0 px-2 md:px-3"
                    title="Ordenar"
                    data-testid="ordenar-select"
                  >
                    <ArrowUpDown className="w-4 h-4 shrink-0 md:hidden" strokeWidth={1.5} aria-hidden />
                    <span className="hidden md:inline"><SelectValue /></span>
                  </SelectTrigger>
                  <SelectContent align="end" className="z-[110]">
                    <SelectItem value="nuevos" className="text-xs">Nuevos primero</SelectItem>
                    <SelectItem value="viejos" className="text-xs">Viejos primero</SelectItem>
                    <SelectItem value="alfabetico" className="text-xs">Alfabético (A-Z)</SelectItem>
                    <SelectItem value="puntuacion_mayor" className="text-xs">Puntuación mayor</SelectItem>
                    <SelectItem value="puntuacion_menor" className="text-xs">Puntuación menor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* IA Search Bar (solo móvil) */}
      {showIaSearch && (
      <div className="md:hidden border-b bg-muted/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <form
            onSubmit={handleIASearch}
            className={`relative flex items-center rounded-md bg-background h-10 overflow-hidden ${
              isSearchingIA
                ? "ia-search-loading"
                : "border border-muted-foreground/25 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-muted-foreground/25 has-[:focus-visible]:ring-offset-2"
            }`}
          >
            <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" strokeWidth={1.5} />
            <Input
              placeholder="Buscar con IA: ej. 'Diseñador senior con experiencia en branding...'"
              value={iaQuery}
              onChange={(e) => setIaQuery(e.target.value)}
              className="pl-10 pr-20 h-10 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
              data-testid="ia-search-input-mobile"
            />
            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
              {iaResults && (
                <button
                  type="button"
                  onClick={clearIASearch}
                  className="p-2 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
                  aria-label="Limpiar búsqueda"
                  data-testid="clear-ia-btn-mobile"
                >
                  <X className="w-4 h-4" strokeWidth={1.5} />
                </button>
              )}
              <button
                type="submit"
                disabled={isSearchingIA || !iaQuery.trim()}
                className="p-2 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50 disabled:pointer-events-none"
                aria-label="Buscar"
                data-testid="ia-search-btn-mobile"
              >
                {isSearchingIA ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" strokeWidth={1.5} />}
              </button>
            </div>
          </form>
        </div>
      </div>
      )}

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 md:pb-24 min-w-0">
        <div
          key={tabListas ? "listas" : tabFavoritos ? "favoritos" : !tabActivos ? "archivados" : "activos"}
          className="animate-fade-in min-w-0"
        >
        {/* Vista Listas */}
        {tabListas && !isSearchingIA && !iaResults && (
          listaActiva ? (
            /* Detalle de una lista: durante la carga solo loader; al terminar, breadcrumb + contenido con fade-in al unísono */
            isLoadingCandidatosLista ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
            <div className="pt-0 pb-6 flex flex-col w-full">
              <div className="w-full order-2" style={{ position: 'relative', zIndex: 0 }}>
              {(() => {
                const candidatosListaFiltrados = listaCarpetaTab
                  ? listaCarpetaTab === "Descartados"
                    ? candidatosListaConFiltros.filter((c) => c.carpeta === "Descartados")
                    : listaCarpetaTab === "Revisados"
                      ? candidatosListaConFiltros.filter((c) => c.carpeta === "Revisados")
                      : candidatosListaConFiltros.filter((c) => !c.carpeta || c.carpeta === "Por revisar")
                  : candidatosListaConFiltros;
                return candidatosListaFiltrados.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 min-h-[220px] w-full text-center">
                    <Users className="w-12 h-12 text-muted-foreground/50 mb-4 shrink-0" strokeWidth={1} />
                    <p className="text-sm text-muted-foreground">
                      {listaActiva?.todos_activos && listaCarpetaTab
                        ? listaCarpetaTab === "Descartados"
                          ? "No hay candidatos descartados"
                          : listaCarpetaTab === "Revisados"
                            ? "No hay candidatos revisados"
                            : "No hay candidatos por revisar"
                        : "Esta lista está vacía"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 max-w-sm">
                      {listaActiva?.todos_activos
                        ? (listaCarpetaTab === "Descartados" || listaCarpetaTab === "Revisados"
                          ? "Clic derecho en una tarjeta → Restaurar para volver a Por revisar."
                          : "Clic derecho en una tarjeta → Marcar como revisado o Descartar.")
                        : "Añade candidatos desde su página de detalle"}
                    </p>
                  </div>
                ) : listaCarpetaTab === "Revisados" ? (
                <div className="space-y-8">
                  {listaActiva?.carpetas?.length > 0 && !listaActiva?.todos_activos && (
                    <p className="text-xs text-muted-foreground">
                      Mueve cada candidato a una carpeta: &quot;Por revisar&quot;, &quot;Revisados&quot; o &quot;Descartados&quot;.
                    </p>
                  )}
                  {(() => {
                    const byRevisador = {};
                    const sinRevisador = [];
                    candidatosListaFiltrados.forEach((c) => {
                      const email = c.revisador || "";
                      if (!email) {
                        sinRevisador.push(c);
                        return;
                      }
                      if (!byRevisador[email]) byRevisador[email] = [];
                      byRevisador[email].push(c);
                    });
                    const revisadorEmails = Object.keys(byRevisador).filter((e) => (byRevisador[e]?.length || 0) > 0);
                    const getRevisadorNombre = (email) => {
                      const nombre = (listaActiva?.revisador_detalles?.[email]?.nombre || "").trim();
                      if (nombre) return nombre;
                      const parte = (email || "").split("@")[0] || "?";
                      return parte.charAt(0).toUpperCase() + parte.slice(1).toLowerCase();
                    };
                    revisadorEmails.sort((a, b) => getRevisadorNombre(a).localeCompare(getRevisadorNombre(b), undefined, { sensitivity: "base" }));
                    const renderCard = (candidato, index) => (
                      <div key={candidato.id} className="animate-card-in h-full" style={{ animationDelay: `${index * 40}ms` }}>
                        <CandidatoCardWithCompare
                          candidato={candidato}
                          revisadorInicial={null}
                          onClick={() => navigate(`/candidatos/${candidato.id}`, { state: { fromListaId: listaActiva?.id, fromListaTodosActivos: listaActiva?.todos_activos, candidatoIds: candidatosListaFiltrados.map((c) => c.id), candidatoCarpeta: candidato.carpeta, candidatoRevisador: candidato.revisador, fromListaCarpetaTab: listaCarpetaTab } })}
                          isSelectionMode={batchArchiveMode}
                          isSelected={selectedIds.includes(candidato.id)}
                          onToggleSelect={() => toggleSelectId(candidato.id)}
                          onToggleFavorito={() => handleToggleFavorito(candidato.id, candidato.favorito)}
                          onRevisado={undefined}
                          onDescartar={listaActiva?.todos_activos ? () => handleSetCarpeta(candidato.id, "Descartados") : undefined}
                          onRestaurar={listaActiva?.todos_activos ? () => handleSetCarpeta(candidato.id, "Por revisar") : undefined}
                        />
                      </div>
                    );
                    return revisadorEmails.length === 0 && sinRevisador.length === 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {candidatosListaFiltrados.map((candidato, index) => renderCard(candidato, index))}
                      </div>
                    ) : (
                      <>
                      {revisadorEmails.map((email) => {
                        const candidatosGrupo = byRevisador[email];
                        const nombreRevisador = getDisplayName(email) ?? getRevisadorNombre(email);
                        return (
                          <div key={email} className="space-y-3">
                            <p className="text-sm font-medium text-foreground">{nombreRevisador}</p>
                            <div className="border-b border-border mb-4" aria-hidden />
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                              {candidatosGrupo.map((candidato, index) => renderCard(candidato, index))}
                            </div>
                          </div>
                        );
                      })}
                      {sinRevisador.length > 0 && (
                        <div className="space-y-3">
                          <p className="text-sm font-medium text-muted-foreground">Sin asignar</p>
                          <div className="border-b border-border mb-4" aria-hidden />
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {sinRevisador.map((candidato, index) => renderCard(candidato, index))}
                          </div>
                        </div>
                      )}
                      </>
                    );
                  })()}
                </div>
                ) : (
                <div className="space-y-6">
                  {listaActiva?.carpetas?.length > 0 && !listaActiva?.todos_activos && (
                    <p className="text-xs text-muted-foreground">
                      Mueve cada candidato a una carpeta: &quot;Por revisar&quot;, &quot;Revisados&quot; o &quot;Descartados&quot;.
                    </p>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {candidatosListaFiltrados.map((candidato, index) => (
                      <div key={candidato.id} className="animate-card-in h-full" style={{ animationDelay: `${index * 40}ms` }}>
                        <CandidatoCardWithCompare
                          candidato={candidato}
                          revisadorInicial={(listaCarpetaTab === "Revisados" || listaCarpetaTab === "Descartados") ? null : (listaActiva ? getRevisadorInicial(candidato) : null)}
                          onClick={() => navigate(`/candidatos/${candidato.id}`, { state: { fromListaId: listaActiva?.id, fromListaTodosActivos: listaActiva?.todos_activos, candidatoIds: candidatosListaFiltrados.map((c) => c.id), candidatoCarpeta: candidato.carpeta, candidatoRevisador: candidato.revisador, fromListaCarpetaTab: listaCarpetaTab } })}
                          isSelectionMode={batchArchiveMode}
                          isSelected={selectedIds.includes(candidato.id)}
                          onToggleSelect={() => toggleSelectId(candidato.id)}
                          onToggleFavorito={() => handleToggleFavorito(candidato.id, candidato.favorito)}
                          onRevisado={listaActiva?.todos_activos && listaCarpetaTab !== "Revisados" ? () => handleSetCarpeta(candidato.id, "Revisados") : undefined}
                          onDescartar={listaActiva?.todos_activos && listaCarpetaTab !== "Descartados" ? () => handleSetCarpeta(candidato.id, "Descartados") : undefined}
                          onRestaurar={listaActiva?.todos_activos && (listaCarpetaTab === "Descartados" || listaCarpetaTab === "Revisados") ? () => handleSetCarpeta(candidato.id, "Por revisar") : undefined}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                );
              })()}
              </div>
            </div>
            )
          ) : (tabFavoritos || !tabActivos) ? (
            /* Favoritos o Archivados dentro de Listas (solo móvil: se llega desde Listas) */
            <div className="pt-2 pb-6 animate-fade-in">
              <div className="md:hidden mb-4">
                <button
                  type="button"
                  onClick={() => { setTabFavoritos(false); setTabActivos(true); }}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
                  Listas
                </button>
              </div>
              {isLoading ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : !candidatosOrdenados.length ? (
                <div className="text-center py-20">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" strokeWidth={1} />
                  <p className="text-sm text-muted-foreground">
                    {tabFavoritos ? "No tienes candidatos en favoritos" : "No hay candidatos archivados"}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="candidatos-grid">
                  {candidatosOrdenados.map((candidato, index) => (
                    <div key={candidato.id} className="animate-card-in h-full" style={{ animationDelay: `${index * 40}ms` }}>
                      <CandidatoCardWithCompare
                        candidato={candidato}
                        onClick={() => navigate(`/candidatos/${candidato.id}`, { state: { candidatoIds: candidatosOrdenados.map((c) => c.id) } })}
                        onToggleFavorito={() => handleToggleFavorito(candidato.id, candidato.favorito)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Lista de listas (en móvil incluye Favoritos y Archivados arriba) */
            <div className="pt-0 pb-6 animate-fade-in">
              {/* Favoritos y Archivados dentro de Listas (solo responsive) */}
              <div className="md:hidden space-y-2 mb-6">
                <button
                  type="button"
                  onClick={() => { setTabFavoritos(true); setTabActivos(true); setListaActiva(null); }}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-input bg-card hover:bg-muted/30 transition-colors text-left"
                >
                  <Heart className="w-5 h-5 text-red-500 shrink-0" strokeWidth={1.5} />
                  <span className="text-sm font-medium">Favoritos</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setTabActivos(false); setTabFavoritos(false); setListaActiva(null); }}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-input bg-card hover:bg-muted/30 transition-colors text-left"
                >
                  <Archive className="w-5 h-5 text-muted-foreground shrink-0" strokeWidth={1.5} />
                  <span className="text-sm font-medium">Archivados</span>
                </button>
              </div>
              <div className="flex items-center justify-between gap-3 mb-4">
                <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Mis listas</h2>
                <Button
                  size="sm"
                  variant="secondary"
                  className="h-9 text-xs shrink-0"
                  onClick={() => {
                    setEditandoListaId(null);
                    setEditandoListaVersion(null);
                    setNuevaListaNombre("");
                    setNuevaListaEmoji("📁");
                    setListaTodosActivosForm(false);
                    setOpenAddListaDialog(true);
                  }}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
                  Nueva lista
                </Button>
              </div>
              {isLoadingListas ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : listas.length === 0 ? (
                <div className="text-center py-20">
                  <ListChecks className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" strokeWidth={1} />
                  <p className="text-sm text-muted-foreground">Aún no hay listas</p>
                  <p className="text-xs text-muted-foreground mt-1">Crea una para organizar tus candidatos favoritos</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {orderedListas.map((lista) => {
                    const isRecienCreada = lista.id === listaRecienCreadaId;
                    const isEliminando = listasEliminando.includes(lista.id);
                    return (
                    <div
                      key={lista.id}
                      className={`border border-input rounded-lg bg-card transition-colors ${
                        isEliminando ? "animate-fade-out pointer-events-none" : isRecienCreada ? "animate-fade-in" : "hover:bg-muted/30"
                      }`}
                    >
                      {editandoListaId === lista.id ? (
                        <div className="p-4 flex items-center gap-2">
                          <Input
                            autoFocus
                            value={editandoListaNombre}
                            onChange={(e) => setEditandoListaNombre(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleRenombrarLista(lista.id);
                              if (e.key === "Escape") { setEditandoListaId(null); setEditandoListaNombre(""); }
                            }}
                            className="h-7 text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => handleRenombrarLista(lista.id)}
                            className="p-1 rounded text-muted-foreground hover:text-foreground"
                          >
                            <Check className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                          <button
                            type="button"
                            onClick={() => { setEditandoListaId(null); setEditandoListaNombre(""); }}
                            className="p-1 rounded text-muted-foreground hover:text-foreground"
                          >
                            <X className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        </div>
                      ) : (
                        <div className="p-4 flex items-start justify-between gap-3">
                          <button
                            type="button"
                            className="flex-1 text-left"
                            onClick={() => openLista(lista)}
                          >
                            <p className="text-sm font-medium">{lista.nombre}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {(lista.candidatos_count ?? (lista.candidatos_ids || []).length)} candidato{(lista.candidatos_count ?? (lista.candidatos_ids || []).length) !== 1 ? "s" : ""}
                            </p>
                          </button>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              title="Renombrar"
                              onClick={() => { setEditandoListaId(lista.id); setEditandoListaNombre(lista.nombre); }}
                              className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                            >
                              <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </button>
                            <button
                              type="button"
                              title="Eliminar"
                              onClick={() => handleEliminarLista(lista.id)}
                              className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )
        )}
        {!tabListas && isSearchingIA ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Analizando candidatos con IA...</p>
            </div>
          </div>
        ) : !tabListas && iaResults ? (
          /* IA Results */
          <div className="pt-6 md:pt-8 space-y-14 animate-fade-in">
            {iaResults.grupos && iaResults.grupos.map((grupo, index) => (
              <div key={index} className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                    {grupo.titulo}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-2">{grupo.explicacion}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {(grupo.candidatos || []).map((candidato, index) => {
                    const iaCandidatoIds = [
                      ...(iaResults.grupos || []).flatMap((g) => (g.candidatos || []).map((c) => c.id)),
                      ...(iaResults.no_recomendados?.candidatos || []).map((c) => c.id),
                    ];
                    return (
                      <div key={candidato.id} className="animate-card-in h-full" style={{ animationDelay: `${index * 40}ms` }}>
                        <CandidatoCardWithCompare
                          candidato={candidato}
                          onClick={() => navigate(`/candidatos/${candidato.id}`, { state: { candidatoIds: iaCandidatoIds } })}
                          onToggleFavorito={() => handleToggleFavorito(candidato.id, candidato.favorito)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            
            {iaResults.no_recomendados && (iaResults.no_recomendados.candidatos || []).length > 0 && (
              <div className="opacity-60 space-y-6">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-muted-foreground">
                    No recomendados
                  </h2>
                  <p className="text-sm text-muted-foreground mt-2">{iaResults.no_recomendados.explicacion}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {(iaResults.no_recomendados.candidatos || []).map((candidato, index) => {
                    const iaCandidatoIds = [
                      ...(iaResults.grupos || []).flatMap((g) => (g.candidatos || []).map((c) => c.id)),
                      ...(iaResults.no_recomendados?.candidatos || []).map((c) => c.id),
                    ];
                    return (
                      <div key={candidato.id} className="animate-card-in h-full" style={{ animationDelay: `${index * 40}ms` }}>
                        <CandidatoCardWithCompare
                          candidato={candidato}
                          onToggleFavorito={() => handleToggleFavorito(candidato.id, candidato.favorito)}
                          onClick={() => navigate(`/candidatos/${candidato.id}`, { state: { candidatoIds: iaCandidatoIds } })}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {(!iaResults.grupos || iaResults.grupos.length === 0) && (
              <div className="text-center py-20">
                <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" strokeWidth={1} />
                <p className="text-sm text-muted-foreground">
                  No se encontraron candidatos que encajen con tu búsqueda
                </p>
              </div>
            )}
          </div>
        ) : tabListas ? null : isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : !candidatosOrdenados.length ? (
          <div className="text-center py-20">
            <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" strokeWidth={1} />
            <p className="text-sm text-muted-foreground">
              {hasActiveFilters ? "No se encontraron candidatos con los filtros aplicados" : "Aún no hay candidatos"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="candidatos-grid">
            {candidatosOrdenados.map((candidato, index) => (
              <div key={candidato.id} className="animate-card-in h-full" style={{ animationDelay: `${index * 40}ms` }}>
                <CandidatoCardWithCompare
                  candidato={candidato}
                  onClick={() => navigate(`/candidatos/${candidato.id}`, { state: { candidatoIds: candidatosOrdenados.map((c) => c.id) } })}
                  isSelectionMode={batchArchiveMode && tabActivos && !tabFavoritos}
                  isSelected={selectedIds.includes(candidato.id)}
                  onToggleSelect={() => toggleSelectId(candidato.id)}
                  onToggleFavorito={() => handleToggleFavorito(candidato.id, candidato.favorito)}
                />
              </div>
            ))}
          </div>
        )}
        </div>
      </main>

      {/* Barra Buscar con IA fija abajo (solo desktop) */}
      {showIaSearch && (
      <div className="hidden md:block fixed bottom-6 left-0 right-0 z-50 pointer-events-none">
        <div className="max-w-7xl mx-auto px-6 flex justify-center pointer-events-auto">
          <form
            onSubmit={handleIASearch}
            className={`relative w-full max-w-xl flex items-center rounded-lg bg-background shadow-lg h-11 overflow-hidden ${
              isSearchingIA
                ? "ia-search-loading"
                : "border border-muted-foreground/25 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-muted-foreground/25 has-[:focus-visible]:ring-offset-2"
            }`}
          >
            <Sparkles className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground shrink-0 pointer-events-none" strokeWidth={1.5} />
            <Input
              placeholder="Buscar con IA: ej. 'Diseñador senior con experiencia en branding...'"
              value={iaQuery}
              onChange={(e) => setIaQuery(e.target.value)}
              className="pl-10 pr-20 h-11 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
              data-testid="ia-search-input"
            />
            <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
              {iaResults && (
                <button
                  type="button"
                  onClick={clearIASearch}
                  className="p-2 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
                  aria-label="Limpiar búsqueda"
                  data-testid="clear-ia-btn"
                >
                  <X className="w-4 h-4" strokeWidth={1.5} />
                </button>
              )}
              <button
                type="submit"
                disabled={isSearchingIA || !iaQuery.trim()}
                className="p-2 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50 disabled:pointer-events-none"
                aria-label="Buscar"
                data-testid="ia-search-btn"
              >
                {isSearchingIA ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" strokeWidth={1.5} />}
              </button>
            </div>
          </form>
        </div>
      </div>
      )}

      <Drawer open={mobileViewsOpen} onOpenChange={setMobileViewsOpen} shouldScaleBackground={false}>
        <DrawerContent className="max-h-[85dvh] pb-[env(safe-area-inset-bottom)]">
          <DrawerHeader className="pb-0">
            <DrawerTitle>Vistas y listas</DrawerTitle>
          </DrawerHeader>
          <div className="flex flex-col min-h-0 overflow-hidden">{sidebarBody}</div>
        </DrawerContent>
      </Drawer>

      <Dialog open={openAddListaDialog} onOpenChange={setOpenAddListaDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editandoListaId ? "Editar lista" : "Añadir lista"}</DialogTitle>
            <DialogDescription>Configura nombre, emoji y opción "Todos".</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Popover open={emojiPickerOpen} onOpenChange={setEmojiPickerOpen}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" className="h-10 w-10 p-0 text-lg">
                    {nuevaListaEmoji || "📁"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 border-0" align="start">
                  <EmojiPicker
                    onEmojiClick={(emojiData) => {
                      setNuevaListaEmoji(emojiData.emoji);
                      setEmojiPickerOpen(false);
                    }}
                    theme="light"
                  />
                </PopoverContent>
              </Popover>
              <Input
                placeholder="Nombre de la lista..."
                value={nuevaListaNombre}
                onChange={(e) => setNuevaListaNombre(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCrearLista();
                }}
                autoFocus
              />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <Label htmlFor="lista-todos-form" className="text-sm">Todos</Label>
              <Switch
                id="lista-todos-form"
                checked={!!listaTodosActivosForm}
                onCheckedChange={setListaTodosActivosForm}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setOpenAddListaDialog(false);
                setEditandoListaId(null);
                setEditandoListaVersion(null);
                setNuevaListaNombre("");
                setNuevaListaEmoji("📁");
                setListaTodosActivosForm(false);
              }}
            >
              Cancelar
            </Button>
            <Button onClick={handleCrearLista} disabled={!nuevaListaNombre.trim() || isCreandoLista}>
              {isCreandoLista ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editandoListaId ? "Guardar cambios" : "Crear lista"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={adminDialogOpen} onOpenChange={(open) => { setAdminDialogOpen(open); if (!open) setAdminPasswordError(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Acceso Admin</DialogTitle>
            <DialogDescription>
              Introduce la contraseña para acceder al panel de administración.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Input
              type="password"
              placeholder="Contraseña"
              value={adminPassword}
              onChange={(e) => { setAdminPassword(e.target.value); setAdminPasswordError(""); }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAdminSubmit();
                }
              }}
              autoFocus
            />
            {adminPasswordError && (
              <p className="text-sm text-destructive">{adminPasswordError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdminDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAdminSubmit}>
              Entrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {(() => {
        const nombrePreview = (emailDescartadosTestNombre || "").trim() || "Nombre de prueba";
        return (
      <Dialog
        open={emailDescartadosOpen}
        onOpenChange={(open) => {
          if (!open) saveEmailDescartadosDraft();
          setEmailDescartadosOpen(open);
          setEmailDescartadosConfirmSend(false);
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Enviar correo a descartados</DialogTitle>
            <DialogDescription>
              Plantilla HTML con <code className="text-xs bg-muted px-1 rounded">{'{{nombre}}'}</code> / <code className="text-xs bg-muted px-1 rounded">{'{{name}}'}</code> y <code className="text-xs bg-muted px-1 rounded">{'{{email}}'}</code>. El asunto también admite estas variables.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <div className="space-y-2">
              <Label htmlFor="email-descartados-asunto">Asunto</Label>
              <Input
                id="email-descartados-asunto"
                value={emailDescartadosSubject}
                onChange={(e) => setEmailDescartadosSubject(e.target.value)}
                placeholder="Comunicación sobre tu candidatura"
              />
              <p className="text-xs text-muted-foreground">
                Crea una plantilla en{" "}
                <a href="https://beefree.io/" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline">
                  beefree.io
                </a>{" "}
                y copia el código aquí.
              </p>
            </div>
            <Tabs value={emailDescartadosPreviewTab} onValueChange={setEmailDescartadosPreviewTab}>
              <TabsList>
                <TabsTrigger value="plantilla">Plantilla HTML</TabsTrigger>
                <TabsTrigger value="vista-previa">Vista previa</TabsTrigger>
                <TabsTrigger value="destinatarios">Destinatarios</TabsTrigger>
              </TabsList>
              <TabsContent value="plantilla" className="mt-2">
                <Textarea
                  id="email-descartados-plantilla"
                  value={emailDescartadosTemplate}
                  onChange={(e) => setEmailDescartadosTemplate(e.target.value)}
                  placeholder="<p>Hola {{nombre}}, ...</p>"
                  className="min-h-[180px] font-mono text-sm"
                />
              </TabsContent>
              <TabsContent value="vista-previa" className="mt-2">
                <div className="rounded border bg-muted/30 overflow-hidden min-h-[220px]">
                  <p className="text-xs text-muted-foreground p-2 border-b bg-background">
                    Asunto: {(emailDescartadosSubject || "").replace(/\{\{nombre\}\}/g, nombrePreview).replace(/\{\{name\}\}/g, nombrePreview).replace(/\{\{email\}\}/g, "prueba@ejemplo.com")}
                  </p>
                  <div className="overflow-auto max-h-[360px] p-4 bg-white dark:bg-zinc-900">
                    <div
                      className="email-preview-wrapper mx-auto w-full max-w-[100%] min-w-0"
                      style={{ maxWidth: "min(100%, 600px)" }}
                      dangerouslySetInnerHTML={{
                        __html: (emailDescartadosTemplate || "")
                          .replace(/\{\{nombre\}\}/g, nombrePreview)
                          .replace(/\{\{name\}\}/g, nombrePreview)
                          .replace(/\{\{email\}\}/g, "prueba@ejemplo.com"),
                      }}
                    />
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="destinatarios" className="mt-2">
                <div className="rounded border bg-muted/30 p-3 min-h-[180px]">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-xs text-muted-foreground">
                      {destinatariosEditList.filter((r) => (r.email || "").trim() && (r.email || "").includes("@")).length} destinatario(s) con email válido. Edita o añade filas.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0 h-8"
                      onClick={handleActualizarDestinatarios}
                      disabled={emailDescartadosRefreshing || !listaActiva?.id}
                    >
                      {emailDescartadosRefreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                      Actualizar
                    </Button>
                  </div>
                  <div className="overflow-auto max-h-[280px] rounded border bg-background">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left p-2 font-medium">Nombre</th>
                          <th className="text-left p-2 font-medium">Correo</th>
                          <th className="w-10 p-1 text-center">Estado</th>
                          <th className="w-10 p-1" />
                        </tr>
                      </thead>
                      <tbody>
                        {destinatariosEditList.map((row, idx) => {
                          const email = (row.email || "").trim();
                          const isValid = email && email.includes("@");
                          const failedEntry = lastSendResult?.failed?.find((f) => (f.email || "").trim().toLowerCase() === email.toLowerCase());
                          const wasSent = lastSendResult?.attempted?.includes(email);
                          return (
                            <tr key={idx} className="border-b last:border-b-0">
                              <td className="p-1">
                                <Input
                                  value={row.nombre}
                                  onChange={(e) => {
                                    setLastSendResult(null);
                                    setDestinatariosEditList((prev) => prev.map((r, i) => (i === idx ? { ...r, nombre: e.target.value } : r)));
                                  }}
                                  placeholder="Nombre"
                                  className="h-8 text-xs"
                                />
                              </td>
                              <td className="p-1">
                                <Input
                                  type="email"
                                  value={row.email}
                                  onChange={(e) => {
                                    setLastSendResult(null);
                                    setDestinatariosEditList((prev) => prev.map((r, i) => (i === idx ? { ...r, email: e.target.value } : r)));
                                  }}
                                  placeholder="correo@ejemplo.com"
                                  className="h-8 text-xs"
                                />
                              </td>
                              <td className="p-1 text-center">
                                {wasSent && !failedEntry && <CheckCircle2 className="w-4 h-4 text-green-600 inline-block" aria-label="Enviado" />}
                                {failedEntry && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex">
                                        <XCircle className="w-4 h-4 text-destructive" aria-label="Error" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="left">{failedEntry.error}</TooltipContent>
                                  </Tooltip>
                                )}
                              </td>
                              <td className="p-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                  onClick={() => {
                                    setLastSendResult(null);
                                    setDestinatariosEditList((prev) => prev.filter((_, i) => i !== idx));
                                  }}
                                  aria-label="Quitar fila"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      setLastSendResult(null);
                      setDestinatariosEditList((prev) => [...prev, { nombre: "", email: "" }]);
                    }}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    Añadir fila
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
              <Label htmlFor="email-descartados-test-nombre" className="sr-only">Nombre de prueba</Label>
              <Input
                id="email-descartados-test-nombre"
                placeholder="Nombre de prueba"
                value={emailDescartadosTestNombre}
                onChange={(e) => setEmailDescartadosTestNombre(e.target.value)}
                className="w-40 h-9"
              />
              <Label htmlFor="email-descartados-test" className="sr-only">Email de prueba</Label>
              <Input
                id="email-descartados-test"
                type="email"
                placeholder="Email de prueba"
                value={emailDescartadosTestTo}
                onChange={(e) => setEmailDescartadosTestTo(e.target.value)}
                className="w-48 h-9"
              />
              <Button
                type="button"
                variant="outline"
                className="h-9"
                onClick={handleEmailDescartadosTest}
                disabled={emailDescartadosSendingTest || !(emailDescartadosTestTo || "").trim()}
              >
                {emailDescartadosSendingTest ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Enviar correo de prueba
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setEmailDescartadosOpen(false); setEmailDescartadosConfirmSend(false); }}>
              Cancelar
            </Button>
            {lastSendResult?.failed?.length > 0 && !emailDescartadosConfirmSend && (
              <Button
                type="button"
                variant="outline"
                onClick={handleEmailDescartadosSendOnlyFailed}
                disabled={emailDescartadosSendingAll || emailDescartadosSendingFailed}
              >
                {emailDescartadosSendingFailed ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Enviar solo a fallidos ({lastSendResult.failed.length})
              </Button>
            )}
            {emailDescartadosConfirmSend ? (
              <>
                <Button variant="outline" onClick={() => setEmailDescartadosConfirmSend(false)}>
                  Atrás
                </Button>
                <Button onClick={handleEmailDescartadosSendAll} disabled={emailDescartadosSendingAll}>
                  {emailDescartadosSendingAll ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Confirmar envío a {destinatariosEditList.filter((r) => (r.email || "").trim() && (r.email || "").includes("@")).length} destinatarios
                </Button>
              </>
            ) : (
              <Button onClick={handleEmailDescartadosSendAll} disabled={emailDescartadosSendingAll || emailDescartadosSendingFailed}>
                {emailDescartadosSendingAll ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Enviar a todos los destinatarios
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
        );
      })()}
        </div>
      </div>
    </div>
  );
}

function CandidatoCard({ candidato, onClick, isSelectionMode, isSelected, onToggleSelect, onToggleFavorito, showScore = true, revisadorInicial = null, currentUser = null, maskValue = (v) => v }) {
  if (!candidato) return null;
  const ubicacionRaw = [candidato.ciudad, candidato.provincia].filter(Boolean).join(", ");
  const ubicacion = maskValue(ubicacionRaw, "direccion");
  
  const getScoreColors = (score) => {
    if (score >= 75) return "bg-emerald-100 text-emerald-700";
    if (score >= 50) return "bg-amber-100 text-amber-700";
    if (score >= 25) return "bg-orange-100 text-orange-700";
    return "bg-red-100 text-red-700";
  };

  const parseRangoK = (str) => {
    if (!str || typeof str !== "string") return { minK: null, maxK: null };
    const cleaned = str.trim().replace(/k|€/gi, "");
    const parts = cleaned.split(/[-–—\s]+/).map((p) => p.trim().replace(/,/g, "."));
    const nums = parts.map((p) => parseFloat(p, 10)).filter((n) => !Number.isNaN(n) && n > 0);
    if (nums.length === 0) return { minK: null, maxK: null };
    const minRaw = Math.min(...nums);
    const maxRaw = Math.max(...nums);
    const toK = (n) => (n >= 1000 ? n / 1000 : n);
    return { minK: toK(minRaw), maxK: toK(maxRaw) };
  };
  const formatRangoK = (k) => (k % 1 === 0 ? `${k}` : k.toFixed(1));
  const getRangoChipColors = (rangoStr) => {
    const { maxK } = parseRangoK(rangoStr);
    if (maxK == null) return "bg-muted text-muted-foreground";
    if (maxK <= 20) return "bg-emerald-100 text-emerald-700";
    if (maxK <= 30) return "bg-amber-100 text-amber-700";
    return "bg-red-100 text-red-700";
  };
  const getSituacionChipColors = (situacion) => {
    if (!situacion) return "bg-muted text-muted-foreground";
    switch (situacion) {
      case "freelance": return "bg-amber-100 text-amber-800";
      case "asalariado": return "bg-violet-100 text-violet-800";
      case "estudiante": return "bg-teal-100 text-teal-800";
      default: return "bg-slate-200 text-slate-700";
    }
  };
  const getSituacionLabel = (situacion) => {
    if (situacion === "freelance") return "Freelance";
    if (situacion === "asalariado") return "Asalariado/a";
    if (situacion === "estudiante") return "Estudiante";
    return "En paro";
  };
  const displayRangoLabel = (rangoStr) => {
    const { minK, maxK } = parseRangoK(rangoStr);
    if (minK == null && maxK == null) return "";
    if (minK != null && maxK != null && minK !== maxK) return `${formatRangoK(minK)}k - ${formatRangoK(maxK)}k`;
    if (maxK != null) return `${formatRangoK(maxK)}k`;
    return rangoStr || "";
  };
  const getSalaryScoreAdjustment = (rangoStr) => {
    const { maxK } = parseRangoK(rangoStr);
    if (maxK == null) return 0;
    if (maxK <= 20) return 5;
    if (maxK <= 30) return 0;
    return -5;
  };
  const scoreConRango = (candidato.score_agencia || 0) + getSalaryScoreAdjustment(candidato.rango_salarial);

  const handleCardClick = () => {
    if (isSelectionMode) onToggleSelect?.();
    else onClick?.();
  };

  return (
    <div
      className={`border rounded-lg p-5 bg-card transition-colors cursor-pointer group flex flex-col h-full ${isSelectionMode ? "" : "hover:border-muted-foreground/30"} ${isSelected ? "ring-2 ring-primary border-primary" : ""}`}
      onClick={handleCardClick}
      data-testid={`candidato-card-${candidato.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        {isSelectionMode && (
          <Checkbox
            checked={!!isSelected}
            onCheckedChange={() => onToggleSelect?.()}
            onClick={(e) => e.stopPropagation()}
            className="mt-0.5 shrink-0"
            data-testid={`card-checkbox-${candidato.id}`}
          />
        )}
        <div className="min-w-0">
          <h3 className="font-medium text-sm truncate group-hover:text-foreground transition-colors">
            {maskValue(candidato.nombre ?? "", "nombre") || "—"}
          </h3>
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {candidato.rol_principal || "Sin rol definido"}
          </p>
          {ubicacionRaw && (
            <p className="text-xs text-muted-foreground/70 truncate mt-1 flex items-center gap-1">
              <MapPin className="w-3 h-3" strokeWidth={1.5} />
              {ubicacion}
            </p>
          )}
        </div>
        {showScore && (
          <div className="shrink-0">
            <span className={`text-xs font-semibold px-2 py-1 rounded ${getScoreColors(scoreConRango)}`}>
              {scoreConRango}
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {candidato.nivel && (
          <Badge variant="outline" className="text-xs font-normal">
            {candidato.nivel}
          </Badge>
        )}
        {candidato.años_experiencia > 0 && (
          <span className="text-xs text-muted-foreground">
            {candidato.años_experiencia} años exp.
          </span>
        )}
      </div>

      <div className="flex-grow"></div>

      {candidato.segmentos && candidato.segmentos.length > 0 && (
        <div className="mt-4">
          <p className="text-xs text-muted-foreground mb-2">Habilidades</p>
          <div className="flex items-center gap-1.5 overflow-hidden flex-nowrap">
            {candidato.segmentos.slice(0, 3).map((seg, i) => (
              <Badge key={i} variant="secondary" className="text-xs font-normal py-0.5 shrink-0 max-w-[140px] truncate">
                {seg}
              </Badge>
            ))}
            {candidato.segmentos.length > 3 && (
              <Badge variant="secondary" className="text-xs font-normal py-0.5 shrink-0">
                +{candidato.segmentos.length - 3}
              </Badge>
            )}
          </div>
        </div>
      )}
      
      <div className="pt-4 mt-4 border-t flex items-center justify-between min-h-10 gap-2">
        <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleFavorito?.(); }}
            className="p-1 rounded transition-colors hover:scale-110 flex items-center justify-center"
            aria-label={candidato.favorito ? "Quitar de favoritos" : "Añadir a favoritos"}
          >
            <Heart
              className={`w-5 h-5 transition-colors ${candidato.favorito ? "fill-red-500 text-red-500" : "text-muted-foreground/30 hover:text-red-400"}`}
              strokeWidth={1.5}
            />
          </button>
          {candidato.favoritos_por_detalle?.length > 0 && (
            <div className="flex items-center -space-x-1.5">
              {candidato.favoritos_por_detalle.map((u) => {
                const displayUser = (currentUser && u.email === currentUser.email)
                  ? { ...u, nombre: currentUser.nombre, color: currentUser.color, avatar_variant: currentUser.avatar_variant }
                  : u;
                return (
                  <Tooltip key={u.email}>
                    <TooltipTrigger asChild>
                      <span className="inline-block ring-[1.5px] ring-card rounded-full">
                        <UserAvatar user={displayUser} size={16} className="block" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {displayUser.nombre || displayUser.email}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 justify-end min-w-0 flex-1">
          {revisadorInicial && (
            <span
              className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold text-white shrink-0"
              style={{ backgroundColor: revisadorInicial.color }}
              title="Revisador"
            >
              {revisadorInicial.inicial}
            </span>
          )}
          {candidato.rango_salarial && parseRangoK(candidato.rango_salarial).maxK != null && (
            <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded shrink-0 ${getRangoChipColors(candidato.rango_salarial)}`}>
              {displayRangoLabel(candidato.rango_salarial)}
            </span>
          )}
          <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded shrink-0 ${getSituacionChipColors(candidato.situacion_actual || "ninguno")}`}>
            {getSituacionLabel(candidato.situacion_actual || "ninguno")}
          </span>
          {candidato.contactado ? (
            <span className="inline-flex items-center justify-center w-7 h-7 rounded bg-blue-100 text-blue-700 shrink-0" aria-label="Contactado">
              <PhoneCall className="w-3.5 h-3.5" strokeWidth={1.5} />
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
